/**
 * Atlas PM · 任务设计器 · 提交编排（P3.7b）
 *
 * 提交顺序（后端约定，不可乱）：
 *   1. POST /tasks                                   → 任务主体
 *   2. POST /tasks/:taskId/metrics                   → 每个指标，拿 metricId
 *   3. POST /metrics/:metricId/elements              → 每个要素
 *
 * ⭐ P6.51：产出物契约整体取消，提交不再请求 /deliverable-contracts。
 *    齐套率改由要素「需要附件」的附件上传情况核算。
 *
 * ⚠️ 部分失败**不静默**：任务创建成功后，指标 / 要素的任何失败
 *    都累计进 `failures` 返回给调用方，由 UI 明确提示「任务已创建，但 N 项失败」
 *    并给出继续编辑入口。
 */

import {
  TASK_PRIORITY,
  type CreateElementInput,
  type CreateMetricInput,
  type ElementInfo,
  type MetricInfo,
  type TaskDetail,
  type UpdateElementInput,
  type UpdateMetricInput,
} from '@atlaspm/shared';
import { api } from '../../api/client';
import {
  draftKey,
  type ElementDraft,
  type MetricDraft,
  type TaskMetaDraft,
} from './types';

export interface SubmitDesignInput {
  task: TaskMetaDraft;
  metrics: MetricDraft[];
}

export interface SubmitFailure {
  kind: 'metric' | 'element';
  name: string;
  message: string;
  /** P6.50：后端错误码（如 AUTH_PERMISSION_DENIED），用于区分失败类型给不同引导 */
  code?: string;
}

/**
 * P6.50：是否为「权限类」失败。
 *
 * 后端权限守卫返回 code=AUTH_PERMISSION_DENIED、message 含「无权限」；
 * 兼容仅有文案的历史情况。
 */
export function isPermissionFailure(failure: SubmitFailure): boolean {
  return failure.code === 'AUTH_PERMISSION_DENIED' || failure.message.includes('无权限');
}

export interface SubmitResult {
  taskId: string;
  metricTotal: number;
  metricCreated: number;
  elementTotal: number;
  elementCreated: number;
  failures: SubmitFailure[];
}

const trimmed = (s: string): string => (s ?? '').trim();

/** 空串 → undefined；非法数字 → undefined */
function numOrUndefined(s: string): number | undefined {
  const t = trimmed(s);
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

function buildMetricBody(m: MetricDraft, seq: number): CreateMetricInput {
  return {
    name: trimmed(m.name),
    metricType: trimmed(m.metricType) || 'PERCENT',
    unit: trimmed(m.unit) || undefined,
    weight: numOrUndefined(m.weight),
    seq,
  };
}

function buildElementBody(e: MetricDraft['elements'][number], seq: number): CreateElementInput {
  return {
    name: trimmed(e.name),
    description: trimmed(e.description),
    elementType: e.elementType,
    isRequired: e.isRequired,
    weight: e.weight,
    completionPct: e.completionPct,
    qualityPct: e.qualityPct,
    requireAttachment: e.requireAttachment,
    constraints: e.constraints,
    helpText: e.constraintText ? trimmed(e.constraintText).slice(0, 400) : undefined,
    seq,
  };
}

/** 单个异步步骤的错误文案 */
function errMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message?: unknown }).message ?? '未知错误');
  }
  return String(err);
}

/** 单个异步步骤的后端错误码（ApiError.code） */
function errCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/**
 * 执行完整提交。任务主体失败会抛出（阻断）；子资源失败只累计。
 */
export async function submitTaskDesign(input: SubmitDesignInput): Promise<SubmitResult> {
  const created = await api.post<{ id: string }>('/tasks', {
    projectId: input.task.projectId,
    name: trimmed(input.task.name),
    ownerId: input.task.ownerId,
    description: trimmed(input.task.description) || undefined,
    categoryId: trimmed(input.task.categoryId) || undefined,
    priority: numOrUndefined(input.task.priority),
    // ⭐ P7.7：计划起止时间（YYYY-MM-DD）；空串不下发（保持 null）
    planStart: trimmed(input.task.planStart) || undefined,
    planEnd: trimmed(input.task.planEnd) || undefined,
  });

  const taskId = created.id;
  const failures: SubmitFailure[] = [];
  let metricCreated = 0;
  let elementCreated = 0;
  let elementTotal = 0;

  for (let i = 0; i < input.metrics.length; i += 1) {
    const m = input.metrics[i];
    if (!m) continue;
    elementTotal += m.elements.length;
    let metricId: string;
    try {
      const metric = await api.post<{ id: string }>(
        `/tasks/${taskId}/metrics`,
        buildMetricBody(m, i + 1),
      );
      metricId = metric.id;
      metricCreated += 1;
    } catch (err) {
      failures.push({
        kind: 'metric',
        name: m.name,
        message: errMessage(err),
        code: errCode(err),
      });
      continue;
    }

    for (let j = 0; j < m.elements.length; j += 1) {
      const e = m.elements[j];
      if (!e) continue;
      try {
        await api.post(`/metrics/${metricId}/elements`, buildElementBody(e, j + 1));
        elementCreated += 1;
      } catch (err) {
        failures.push({
          kind: 'element',
          name: `${m.name} / ${e.name}`,
          message: errMessage(err),
          code: errCode(err),
        });
      }
    }
  }

  return {
    taskId,
    metricTotal: input.metrics.length,
    metricCreated,
    elementTotal,
    elementCreated,
    failures,
  };
}

/* ═══════════════════════════════════════════════════════════
   ⭐ P7.5 · 编辑任务（提交审批前可编辑）
   ═══════════════════════════════════════════════════════════ */

/** 编辑加载：任务基本信息 + 指标/要素（回填为设计器草稿） */
export interface TaskDesignSnapshot {
  task: TaskMetaDraft;
  metrics: MetricDraft[];
}

function elementToDraft(e: ElementInfo): ElementDraft {
  return {
    key: draftKey('e'),
    id: e.id,
    name: e.name,
    description: e.description ?? '',
    elementType: e.elementType,
    uiLabel: '',
    isRequired: e.isRequired,
    weight: e.weight,
    completionPct: e.completionPct,
    qualityPct: e.qualityPct,
    requireAttachment: e.requireAttachment,
    constraints: e.constraints,
    constraintText: e.helpText ?? '',
    helpText: e.helpText ?? undefined,
  };
}

function metricToDraft(m: MetricInfo): MetricDraft {
  return {
    key: draftKey('m'),
    id: m.id,
    name: m.name,
    metricType: m.metricType,
    unit: m.unit ?? '',
    weight: String(m.weight ?? 0),
    // ⭐ P7.11：存量指标的权重是既有数据，标记为「用户已设置」，
    //    避免编辑模式下新增指标时被自动平摊逻辑改写。
    weightTouched: true,
    elements: m.elements.map(elementToDraft),
  };
}

/**
 * 编辑模式首屏加载：读任务详情 + 指标（含要素/填写值），映射为设计器草稿。
 *
 * ⚠️ 回填必须带上后端 id（指标 / 要素），否则无法做差异比对，会退化为
 *    「全删再全建」，导致用户已填的完成度 / 质量被级联删除。
 */
export async function fetchTaskDesign(taskId: string): Promise<TaskDesignSnapshot> {
  const [task, metrics] = await Promise.all([
    api.get<TaskDetail>(`/tasks/${taskId}`),
    api.get<MetricInfo[]>(`/tasks/${taskId}/metrics`),
  ]);
  return {
    task: {
      projectId: task.projectId,
      name: task.name,
      ownerId: task.ownerId,
      categoryId: task.categoryId ?? '4',
      categoryName: task.categoryName ?? null,
      description: task.description ?? '',
      priority: String(task.priority ?? TASK_PRIORITY.MEDIUM),
      // ⭐ P7.7：回填计划起止（后端 dateOnly 已归一为 YYYY-MM-DD，可直接喂 input[type=date]）
      planStart: task.planStart ?? '',
      planEnd: task.planEnd ?? '',
    },
    metrics: (metrics ?? []).map(metricToDraft),
  };
}

export interface UpdateDesignInput {
  taskId: string;
  /** 表单当前值（含后端 id；无 id 者为本次新增） */
  task: TaskMetaDraft;
  metrics: MetricDraft[];
  /**
   * 打开编辑时的原始快照（含后端 id 与用户已填的完成度/质量）。
   * 用途：识别被移除的存量指标/要素（用于删除 + 二次确认）。
   * ⚠️ 完成度/质量在任何情况下都不下发，故快照不用于其比对。
   */
  originalMetrics: MetricDraft[];
}

/** 更新结果：更新 / 新增 / 删除 各计数 + 失败清单（沿用 SubmitResult 风格） */
export interface UpdateDesignResult {
  taskId: string;
  metricCreated: number;
  metricUpdated: number;
  metricDeleted: number;
  elementCreated: number;
  elementUpdated: number;
  elementDeleted: number;
  failures: SubmitFailure[];
}

/**
 * P7.5：计算本次保存将删除的存量指标 / 要素数量（用于提交前二次确认）。
 *
 * ⚠️ 指标被删时会级联删除其下要素，故这些要素不再单独计数（避免重复提示）。
 */
export function countRemovals(
  metrics: MetricDraft[],
  originalMetrics: MetricDraft[],
): { metricCount: number; elementCount: number } {
  const formMetricIds = new Set(metrics.map((m) => m.id).filter(Boolean) as string[]);
  const formElementIds = new Set(
    metrics.flatMap((m) => m.elements.map((e) => e.id)).filter(Boolean) as string[],
  );
  let metricCount = 0;
  let elementCount = 0;
  for (const m of originalMetrics) {
    if (m.id && !formMetricIds.has(m.id)) {
      metricCount += 1;
      continue;
    }
    for (const e of m.elements) {
      if (e.id && !formElementIds.has(e.id)) elementCount += 1;
    }
  }
  return { metricCount, elementCount };
}

/**
 * 存量要素的更新体。
 *
 * ⚠️⚠️ 数据安全关键：**只下发【设计字段】**（名称/描述/类型/权重/是否需附件/约束/帮助文本）。
 *    完成度 / 质量是用户在任务详情页填报的原始值（element.completionPct / qualityPct
 *    以及 element_value），**这里绝不携带** —— 避免把用户已填的 60/80 清零或覆盖。
 *    PATCH /elements/:id 只对「请求体中出现的字段」赋值，故省略即不动。
 *    要素值（element_value.valueJson / isComplete / version）本接口也从不触碰。
 */
function buildElementUpdateBody(e: ElementDraft, seq: number): UpdateElementInput {
  return {
    name: trimmed(e.name),
    description: trimmed(e.description),
    elementType: e.elementType,
    isRequired: e.isRequired,
    weight: e.weight,
    requireAttachment: e.requireAttachment,
    constraints: e.constraints,
    helpText: e.constraintText ? trimmed(e.constraintText).slice(0, 400) : undefined,
    seq,
  };
}

/**
 * ⭐ P7.5 编辑保存（差异比对策略）。
 *
 * 顺序：
 *   ① PATCH /tasks/:id                任务基本信息（不含 state）
 *   ② 删除已移除的指标（DELETE /metrics/:id，级联删其要素）
 *   ③ 删除已移除的要素（DELETE /elements/:id）
 *   ④ 逐指标：有 id → PATCH /metrics/:id；无 id → POST /tasks/:taskId/metrics
 *   ⑤ 逐要素：有 id → PATCH /elements/:id；无 id → POST /metrics/:metricId/elements
 *
 * ⚠️ 绝不用「全删旧指标再全建新指标」的简单实现 —— 那会级联删掉用户已填的
 *    完成度 / 质量（element_value）。差异比对只动「真正变化」的部分。
 *
 * 任务主体失败会抛出（阻断）；子资源失败只累计进 failures（不吞错）。
 */
export async function updateTaskDesign(input: UpdateDesignInput): Promise<UpdateDesignResult> {
  const { taskId, task, metrics, originalMetrics } = input;

  // ① 任务基本信息（⚠️ 不传 state / projectId：状态变更与跨项目迁移不在编辑范围）
  await api.patch(`/tasks/${taskId}`, {
    name: trimmed(task.name),
    ownerId: task.ownerId,
    description: trimmed(task.description) || undefined,
    categoryId: trimmed(task.categoryId) || undefined,
    priority: numOrUndefined(task.priority),
    // ⭐ P7.7：计划起止；空串显式下发以支持「清空日期」（后端空串 → null）
    planStart: trimmed(task.planStart),
    planEnd: trimmed(task.planEnd),
  });

  const failures: SubmitFailure[] = [];
  let metricCreated = 0;
  let metricUpdated = 0;
  let metricDeleted = 0;
  let elementCreated = 0;
  let elementUpdated = 0;
  let elementDeleted = 0;

  const formMetricIds = new Set(metrics.map((m) => m.id).filter(Boolean) as string[]);
  const formElementIds = new Set(
    metrics.flatMap((m) => m.elements.map((e) => e.id)).filter(Boolean) as string[],
  );

  // ② 删除已移除的指标（其要素随 FK 级联删除）
  for (const m of originalMetrics) {
    if (!m.id || formMetricIds.has(m.id)) continue;
    try {
      await api.delete(`/metrics/${m.id}`);
      metricDeleted += 1;
    } catch (err) {
      failures.push({ kind: 'metric', name: m.name, message: errMessage(err), code: errCode(err) });
    }
  }

  // ③ 删除已移除的要素（仅限其指标仍存在的；指标已删者已随级联处理）
  for (const m of originalMetrics) {
    if (!m.id || !formMetricIds.has(m.id)) continue;
    for (const e of m.elements) {
      if (!e.id || formElementIds.has(e.id)) continue;
      try {
        await api.delete(`/elements/${e.id}`);
        elementDeleted += 1;
      } catch (err) {
        failures.push({
          kind: 'element',
          name: `${m.name} / ${e.name}`,
          message: errMessage(err),
          code: errCode(err),
        });
      }
    }
  }

  // ④⑤ 逐指标 / 逐要素：更新（有 id）或新建（无 id）
  for (let i = 0; i < metrics.length; i += 1) {
    const m = metrics[i];
    if (!m) continue;
    let metricId = m.id;

    if (metricId) {
      try {
        await api.patch<MetricInfo>(`/metrics/${metricId}`, buildMetricBody(m, i + 1) as UpdateMetricInput);
        metricUpdated += 1;
      } catch (err) {
        failures.push({ kind: 'metric', name: m.name, message: errMessage(err), code: errCode(err) });
        continue;
      }
    } else {
      try {
        const created = await api.post<{ id: string }>(
          `/tasks/${taskId}/metrics`,
          buildMetricBody(m, i + 1),
        );
        metricId = created.id;
        metricCreated += 1;
      } catch (err) {
        failures.push({ kind: 'metric', name: m.name, message: errMessage(err), code: errCode(err) });
        continue;
      }
    }

    for (let j = 0; j < m.elements.length; j += 1) {
      const e = m.elements[j];
      if (!e) continue;
      if (e.id) {
        try {
          await api.patch(`/elements/${e.id}`, buildElementUpdateBody(e, j + 1));
          elementUpdated += 1;
        } catch (err) {
          failures.push({
            kind: 'element',
            name: `${m.name} / ${e.name}`,
            message: errMessage(err),
            code: errCode(err),
          });
        }
      } else if (metricId) {
        try {
          await api.post(`/metrics/${metricId}/elements`, buildElementBody(e, j + 1));
          elementCreated += 1;
        } catch (err) {
          failures.push({
            kind: 'element',
            name: `${m.name} / ${e.name}`,
            message: errMessage(err),
            code: errCode(err),
          });
        }
      }
    }
  }

  return {
    taskId,
    metricCreated,
    metricUpdated,
    metricDeleted,
    elementCreated,
    elementUpdated,
    elementDeleted,
    failures,
  };
}
